import React, { useCallback, useState, useRef } from "react";
import * as d3 from "d3";

const twoColors = ["#64BDE1", "#D5DADC"];
const threeColors = ["#64BDE1", "#5BB280", "#991962"];
const variousColors = [
  "#64BDE1",
  "#D5DADC",
  "#FBD560",
  "#C275A1",
  "#5BB27F",
  "#E5AB5F",
];

export default function DonutPlot({
  dimensions,
  data,
  valueOfInterest,
  text,
  className,
  subtext,
  addPercent,
  centerLabel,
  valueToDisplay,
  keepDataOrder,
  displayValueAsPercent,
  colorSchema,
  noTotal,
}: {
  dimensions: {
    width: number;
    height: number;
    margin: number;
  };
  data: { [key: string]: number };
  valueOfInterest: string;
  text?: string[];
  className?: string;
  subtext?: string[];
  addPercent?: boolean;
  centerLabel: string;
  valueToDisplay?: number;
  keepDataOrder?: boolean;
  displayValueAsPercent?: boolean;
  colorSchema?: string[];
  noTotal?: boolean;
}) {
  const svgRef = React.useRef<SVGSVGElement>(null);
  const [hoveredSegment, setHoveredSegment] = useState<number | null>(null);
  /* eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO(eslint-baseline): address @typescript-eslint/no-unused-vars */
  const [centerTooltipPosition, setCenterTooltipPosition] = useState({
    x: 0,
    y: 0,
  });
  const [showCenterTooltip, setShowCenterTooltip] = useState(false);

  const hoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { width, height, margin } = dimensions;
  const chartWidth = width + margin;
  const chartHeight = height + margin;
  const centerX = chartWidth / 2;
  const centerY = chartHeight / 2;

  const total = Object.values(data).reduce((acc, d) => acc + d, 0);
  // append the svg object to the div called 'my_dataviz'

  // Create dummy data

  const domain = Object.keys(data);
  const plotColors = colorSchema
    ? colorSchema
    : domain.length <= 2
      ? twoColors
      : domain.length <= 3
        ? threeColors
        : variousColors;
  // set the color scale

  const color = d3
    .scaleOrdinal<string, string>()
    .domain(domain)
    .range(plotColors);

  // Compute the position of each group on the pie:
  const transformedData = Object.keys(data).map((key) => ({
    key,
    value: data[key],
  }));
  const pie = d3
    .pie<{ key: string; value: number }>()
    .value(function (d) {
      return d.value;
    })
    .sort((a, b) => {
      if (keepDataOrder) {
        return domain.indexOf(a.key) - domain.indexOf(b.key);
      }
      if (a.key === valueOfInterest) {
        return -1;
      }
      if (b.key === valueOfInterest) {
        return 1;
      }
      return b.value - a.value;
    })
    .startAngle(0)
    .endAngle(Math.PI * 2);

  const data_ready = pie(transformedData);

  // Build the pie chart: Basically, each part of the pie is a path that we build using the arc function.

  const innerRadius = (0.95 * height) / 2;
  const outerRadius = (0.7 * height) / 2;

  const innerHeight = height - (innerRadius - outerRadius);
  const titleFontSize = Math.max(16, Math.round(height * 0.08));
  const valueFontSize = Math.max(26, Math.round(height * 0.13));
  const subtextFontSize = Math.max(14, Math.round(height * 0.067));
  const subtextLineHeight = Math.round(subtextFontSize * 1.2);

  const arcFunction = d3
    .arc<d3.PieArcDatum<{ key: string; value: number }>>()
    .innerRadius((0.95 * height) / 2)
    .outerRadius((0.7 * height) / 2);

  const handleMouseOver = (index: number) => {
    hoverTimeout.current = setTimeout(() => {
      setHoveredSegment(index);
    }, 200);
  };

  const handleMouseOut = () => {
    clearTimeout(hoverTimeout.current ?? undefined);
    setHoveredSegment(null);
  };

  const handleCenterMouseMove = useCallback(() => {
    setCenterTooltipPosition({ x: centerX, y: centerY });
  }, [centerX, centerY]);

  const handleCenterMouseEnter = useCallback(() => {
    clearTimeout(hoverTimeout.current ?? undefined);
    hoverTimeout.current = setTimeout(() => {
      d3.selectAll(".center-tooltip").raise();
      setShowCenterTooltip(true);
    }, 100);
  }, []);

  const handleCenterMouseLeave = useCallback(() => {
    clearTimeout(hoverTimeout.current ?? undefined);
    hoverTimeout.current = setTimeout(() => {
      setShowCenterTooltip(false);
    }, 100);
  }, []);

  return (
    <div className="relative flex items-center justify-center">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${chartWidth} ${chartHeight}`}
        width={chartWidth}
        height={chartHeight}
        preserveAspectRatio="xMidYMid meet"
        className={`${className} plot block`}
        id={className}
        style={{ overflow: "visible" }}
      >
        <g
          onMouseMove={handleCenterMouseMove}
          onMouseEnter={handleCenterMouseEnter}
          onMouseLeave={handleCenterMouseLeave}
        >
          <text
            x={centerX}
            y={innerRadius - outerRadius + (2.1 * innerHeight) / 8 + margin / 2}
            textAnchor="middle"
            alignmentBaseline="middle"
            fontSize={titleFontSize}
            fontFamily="Work Sans"
          >
            {text?.[0]}
          </text>
          <text
            x={centerX}
            y={innerRadius - outerRadius + (2.9 * innerHeight) / 8 + margin / 2}
            textAnchor="middle"
            alignmentBaseline="middle"
            fontSize={titleFontSize}
            fontFamily="Work Sans"
          >
            {text?.[1]}
          </text>

          {valueOfInterest || valueToDisplay ? (
            <text
              x={centerX}
              y={
                innerRadius - outerRadius + (4.5 * innerHeight) / 8 + margin / 2
              }
              textAnchor="middle"
              alignmentBaseline="middle"
              fontSize={valueFontSize}
              fontFamily="Work Sans"
              fontWeight={600}
            >
              {valueToDisplay || data[valueOfInterest]}
              {displayValueAsPercent && "%"}
              {!displayValueAsPercent && !noTotal && total && `/${total}`}
            </text>
          ) : (
            subtext?.map((text, i) => (
              <text
                key={i}
                x={centerX}
                y={
                  i * subtextLineHeight +
                  innerRadius -
                  outerRadius +
                  (4.5 * innerHeight) / 8 +
                  margin / 2
                }
                textAnchor="middle"
                alignmentBaseline="middle"
                fontSize={subtextFontSize}
                fontFamily="Work Sans"
              >
                {text}
              </text>
            ))
          )}
        </g>
        <g transform={`translate(${centerX}, ${centerY})`}>
          {data_ready.map((d, i) => (
            <g
              key={i}
              onMouseOver={() => handleMouseOver(i)}
              onMouseOut={handleMouseOut}
            >
              <path
                className={`donut-slice key-${i}`}
                d={arcFunction(d) || undefined}
                fill={color(d.data.key)}
              />
            </g>
          ))}
          {data_ready.map((d, i) => {
            const isVisible = hoveredSegment === i && d.value > 0;
            const centroid = arcFunction.centroid(d);
            return (
              <g key={`tooltip-${i}`} style={{ pointerEvents: "none" }}>
                <foreignObject
                  x={centroid[0] > 0 ? centroid[0] - 80 : centroid[0] - 40}
                  y={centroid[1] > 20 ? centroid[1] - 60 : centroid[1] - 40}
                  width={120}
                  height={90}
                  style={{
                    overflow: "visible",
                    opacity: isVisible ? 1 : 0,
                    visibility: isVisible ? "visible" : "hidden",
                  }}
                >
                  <div className="flex items-center justify-center w-full h-full">
                    <div className="bg-white bg-opacity-90 rounded-lg shadow-lg p-2 text-center">
                      <div className="font-semibold text-sm">{d.data.key}</div>
                      <div className="text-sm">
                        {addPercent && total > 0
                          ? Math.round((d.data.value / total) * 100)
                          : d.data.value}
                        {addPercent && total > 0 && "%"}
                      </div>
                    </div>
                  </div>
                </foreignObject>
              </g>
            );
          })}
        </g>
      </svg>
      <div
        onMouseEnter={handleCenterMouseEnter}
        onMouseLeave={handleCenterMouseLeave}
        className="center-tooltip absolute top-[50%] left-[50%] -translate-x-[50%] -translate-y-[50%]"
        style={{ zIndex: 1000, opacity: showCenterTooltip ? 1 : 0 }}
      >
        <div className="flex items-center justify-center w-[150px] h-full center-tooltip">
          <div className="bg-white bg-opacity-95 rounded-md border border-gray-300 shadow-lg p-2.5 text-center max-w-full">
            <div className="text-xs">{centerLabel}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
